/**
 * ActivityLog — viewer for user_management.activity_log.
 *
 * Backend (auth_server.py /activity/list) automatically scopes the query:
 *   - admin role: returns all users' activity
 *   - user role:  returns only the current user's activity
 *
 * Filters: action type, time window, free text search.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch, useAuth } from '../../auth/AuthContext';
import './ActivityLog.css';

interface ActivityRow {
  log_id: string;
  user_id: string;
  username: string;
  action: string;
  target_id: string;
  target_label: string;
  metadata: string;
  success: boolean;
  error_msg: string;
  timestamp: string;
}

const ACTION_LABELS: Record<string, string> = {
  onboard_start: 'Started onboard',
  onboard_cancel: 'Cancelled onboard',
  onboard_complete: 'Completed onboard',
  parquet_create: 'Created parquet',
  parquet_edit: 'Edited parquet',
  parquet_download: 'Downloaded parquet',
  parquet_delete: 'Deleted parquet',
  preset_create: 'Created preset',
  preset_edit: 'Edited preset',
  preset_download: 'Downloaded preset',
  preset_delete: 'Deleted preset',
  apikey_create: 'Created API key',
  apikey_delete: 'Deleted API key',
  user_approve: 'Approved user',
  user_unapprove: 'Revoked approval',
};

const ACTION_COLORS: Record<string, string> = {
  onboard_start: '#58a6ff',
  onboard_cancel: '#f0883e',
  onboard_complete: '#3fb950',
  parquet_create: '#3fb950',
  parquet_edit: '#79c0ff',
  parquet_download: '#bc8cff',
  parquet_delete: '#f85149',
  preset_create: '#3fb950',
  preset_edit: '#79c0ff',
  preset_download: '#bc8cff',
  preset_delete: '#f85149',
  apikey_create: '#3fb950',
  apikey_delete: '#f85149',
  user_approve: '#3fb950',
  user_unapprove: '#f0883e',
};

const ACTION_FILTERS = [
  'all',
  'onboard_start',
  'onboard_cancel',
  'onboard_complete',
  'parquet_create',
  'parquet_edit',
  'parquet_download',
  'parquet_delete',
  'preset_create',
  'preset_edit',
  'preset_download',
  'preset_delete',
  'apikey_create',
  'apikey_delete',
  'user_approve',
  'user_unapprove',
];

const DAY_OPTIONS = [1, 7, 30, 90, 365];

export default function ActivityLog() {
  const { session } = useAuth();
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState<number>(30);
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/activity/list?days=${days}&limit=500`);
      if (r.ok) {
        const data = await r.json();
        setRows(data.activity || []);
      }
    } catch {
      /* keep state */
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (actionFilter !== 'all' && r.action !== actionFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !r.username.toLowerCase().includes(q) &&
          !r.target_label.toLowerCase().includes(q) &&
          !r.action.toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [rows, actionFilter, search]);

  const isAdmin = session?.role === 'admin';

  return (
    <div className="al-root">
      <div className="al-header">
        <div>
          <div className="al-title">Activity Log</div>
          <div className="al-sub">
            {isAdmin
              ? 'Showing activity for all users'
              : 'Showing your own activity'}
          </div>
        </div>
        <div className="al-stats">
          <span className="al-stat-val">{filtered.length}</span>
          <span className="al-stat-key">events</span>
        </div>
      </div>

      <div className="al-controls">
        <input
          className="al-search"
          placeholder="Search by user, target, or action..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="al-select"
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
        >
          {ACTION_FILTERS.map((a) => (
            <option key={a} value={a}>
              {a === 'all' ? 'All actions' : ACTION_LABELS[a] || a}
            </option>
          ))}
        </select>
        <select
          className="al-select"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          {DAY_OPTIONS.map((d) => (
            <option key={d} value={d}>
              Last {d} {d === 1 ? 'day' : 'days'}
            </option>
          ))}
        </select>
        <button className="al-refresh" onClick={refresh}>
          ↻ Refresh
        </button>
      </div>

      <div className="al-table-wrap">
        {loading ? (
          <div className="al-empty">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="al-empty">No activity found</div>
        ) : (
          <table className="al-table">
            <thead>
              <tr>
                <th>Time</th>
                {isAdmin && <th>User</th>}
                <th>Action</th>
                <th>Target</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.log_id} className={r.success ? '' : 'al-row-error'}>
                  <td className="al-time">{formatTime(r.timestamp)}</td>
                  {isAdmin && <td className="al-user">{r.username}</td>}
                  <td>
                    <span
                      className="al-action-pill"
                      style={{
                        color: ACTION_COLORS[r.action] || '#8b949e',
                        borderColor:
                          (ACTION_COLORS[r.action] || '#8b949e') + '44',
                        background:
                          (ACTION_COLORS[r.action] || '#8b949e') + '15',
                      }}
                    >
                      {ACTION_LABELS[r.action] || r.action}
                    </span>
                  </td>
                  <td className="al-target" title={r.target_label}>
                    {r.target_label || '—'}
                  </td>
                  <td>
                    {r.success ? (
                      <span className="al-status-ok">✓</span>
                    ) : (
                      <span className="al-status-err" title={r.error_msg}>
                        ✕ {r.error_msg.slice(0, 60)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}
